import { toast } from "react-toastify";
import { action, observable } from "mobx";
import { api } from "../../api";
import { Dayjs } from "dayjs";
import { decodeUtf8String, downloadBinaryFile } from "../../utils";
import { AppStore } from "../AppStore";

/**
 * Classe responsavel pelo gerenciamento do CNDS
*/
export class _ParcelamentoStore {
  obs = observable({
    tasks_parcelamentos: [] as any[],
    parcelamentos: [] as any[],
    parcelas: [] as any[],
    tasks_length: 0 as number,
    open_solicitar: false as boolean,
    open_parcelamentos: false as boolean,
    open_parcelas: false as boolean,
    open_edit_task_parcelamento: false as boolean,
    page_agendamentos: 1 as number,
    index_agendamentos: [] as any,
    length_agendamentos: 0 as number,
    open_iframe: false as boolean,
    base64: "" as string,
    agendamentos_filter: false as boolean,
    credito_usados: 0 as number,
    credito_contratado: 0 as number,
    qrcode: "" as string,
    pix_id: "" as string,
    loading: false as boolean,
    tarefas: [] as any[],
    parcelamentos_length: 0 as number,
    parcelas_length: 0 as number,
    periodoEntrada: null as string[],
    dataEntrada: null as Dayjs,

    data: {} as any,

    loading_arquivos: false as boolean,
    loading_parcelamentos: false as boolean,
    loading_parcelas: false as boolean,
    loading_tasks: false as boolean,
  })

@action.bound
  resetUI() {
    this.obs.tasks_parcelamentos = [];
    this.obs.tasks_length = 0;

    this.obs.parcelamentos = [];
    this.obs.parcelamentos_length = 0;

    this.obs.parcelas = [];
    this.obs.parcelas_length = 0;

    this.obs.open_solicitar = false;
    this.obs.open_parcelamentos = false;
    this.obs.open_parcelas = false;
    this.obs.open_edit_task_parcelamento = false;

    this.obs.periodoEntrada = null;
    this.obs.dataEntrada = null;

    this.obs.open_iframe = false;
    this.obs.base64 = "";

    this.obs.loading = false;
    this.obs.loading_arquivos = false;
    this.obs.loading_parcelamentos = false;
    this.obs.loading_parcelas = false;
    this.obs.loading_tasks = false;
  }

  /**
  * Busca e gerencia as solicitações relacionadas a uma empresa com base em seu CNPJ.
  * Realiza a chamada à API para obter os dados paginados e atualiza os estados observáveis com os resultados.
  * 
  * @param {number} paged Número da página a ser buscada.
  * @param {string} cnpj CNPJ da empresa para filtrar as solicitações.
  * 
  * @example
  * const params = {
  *   page: 1,
  *   limit: 5,
  *   cnpj: "10770872000192"
  * };
  *
  * const response = await api.post('/api/cnds/index', params);
  */
  async handleTasksPaged(paged: number, cnpj: string, situacao: string, tipoParcela: number, tipoSubParcela: string) {
    try {

      this.obs.loading_tasks = true

      const data: any = {
        page: paged,
        limit: 15,
        cnpj: cnpj,
        situacao: situacao
      };

      if (tipoParcela && tipoParcela !== 0) {
        data.tipo_parcela = tipoParcela;
      }

      if (tipoSubParcela && tipoSubParcela !== "") {
        data.tipo_subparcela = tipoSubParcela;
      }

      const resp = await api.post("/api/parcelamentos/index", data);

      this.obs.tasks_parcelamentos = [];
      this.obs.tasks_length = 0;

      if (resp.data.itens.length === 0) {
        toast.warning("Nenhuma parcela foi encontrada, tente novamente mais tarde.", {});
      } else {
        this.obs.tasks_parcelamentos = resp.data.itens;
        this.obs.tasks_length = resp.data.count;
      }

    } catch (ex) {
      toast.error("Erro ao buscar tarefas. Tente novamente mais tarde.", {});
    }

    this.obs.loading_tasks = false
  }

  /**
   * Realiza a busca paginada de parcelamentos associados a uma tarefa específica.
   * 
   * Esta função:
   * - Executa uma chamada à API `/api/parcelamentos/index-parcelamentos` com filtros e paginação.
   * - Atualiza o estado observável (`this.obs`) com os dados retornados da API.
   * - Controla a exibição de um indicador de carregamento durante a requisição.
   * 
   * Parâmetros:
   * - `paged` (`number`): número da página a ser consultada.
   * - `task_id` (`string`): identificador da tarefa para a qual se deseja buscar parcelamentos.
   * - `filter` (`string`): filtro adicional aplicado à busca (ex: tipo de tributo ou status).
   * 
   * Comportamento:
   * - Ativa o estado de carregamento:
   *    - Define `this.obs.loading_parcelamentos = true` para indicar que a busca está em andamento.
   * 
   * - Monta o objeto `data` com os parâmetros:
   *    - `page`: número da página.
   *    - `limit`: número fixo de itens por página (5).
   *    - `task_id`: ID da tarefa.
   *    - `filter`: string de filtro opcional.
   * 
   * - Faz a requisição à API:
   *    - Utiliza `api.post()` para enviar os dados e aguarda a resposta da rota `/api/parcelamentos/index-parcelamentos`.
   * 
   * - Reseta os dados atuais:
   *    - Esvazia `this.obs.parcelamentos`.
   *    - Zera `this.obs.parcelamentos_length`.
   * 
   * - Processa a resposta:
   *    - Se `resp.data.itens.length === 0`:
   *      - Exibe um toast de aviso informando que nenhuma solicitação foi encontrada.
   *    - Caso contrário:
   *      - Atribui os novos parcelamentos a `this.obs.parcelamentos`.
   *      - Define o total de registros em `this.obs.parcelamentos_length`.
   * 
   * - Finaliza a requisição:
   *    - Define `this.obs.loading_parcelamentos = false`, encerrando o estado de carregamento.
   * 
   * Observações:
   * - O `catch` está vazio, ou seja, erros silenciosamente falharão (pode ser interessante adicionar um `console.error` ou exibir feedback).
   * - A função é usada geralmente em componentes que listam parcelamentos associados a tarefas com suporte a paginação.
   * 
   * @param {number} paged Número da página a ser consultada.
   * @param {string} task_id ID da tarefa relacionada.
   * @param {string} filter Filtro adicional aplicado à busca.
   * @returns {Promise<void>} Uma promise que se resolve após a atualização dos dados observáveis.
  */

  async handleTasksPagedParcelamentos(paged: number, task_id: string, referencia: string, status: string) {
    try {
      this.obs.loading_parcelamentos = true;

      const data = {
        page: paged,
        limit: 5,
        task_id,
        referencia: referencia,
        status: status
      };

      let resp = await api.post("/api/parcelamentos/index-parcelamentos", data);

      this.obs.parcelamentos = resp.data.itens || [];
      this.obs.parcelamentos_length = resp.data.count || 0;

      if (resp.data.itens.length === 0) {
        toast.warning("Nenhum parcelamento encontrado.", {});
      }

    } catch (err) {
      toast.error("Erro ao buscar parcelamentos.", {});
    }

    this.obs.loading_parcelamentos = false;
  }

  /**
  * Realiza a busca paginada das parcelas de um parcelamento associado a uma tarefa específica.
  * 
  * Esta função:
  * - Executa uma chamada à API `/api/parcelamentos/index-parcelas` com filtros e paginação.
  * - Atualiza o estado observável (`this.obs`) com os dados retornados da API.
  * 
  * Parâmetros:
  * - `paged` (`number`): número da página a ser consultada.
  * - `task_id` (`string`): identificador da tarefa para a qual se deseja buscar as parcelas.
  * - `tributo` (`string`): tipo de tributo associado ao parcelamento.
  * - `n_parcelamento` (`string`): número do parcelamento (caso não informado, será tratado como `null`).
  * - `dataEntrada` (`string[]`): array contendo as datas de entrada utilizadas como filtro.
  * 
  * Comportamento:
  * - Monta o objeto `data` com os parâmetros fornecidos:
  *    - `page`: número da página.
  *    - `limit`: número fixo de itens por página (5).
  *    - `task_id`: ID da tarefa.
  *    - `tributo`: tipo de tributo.
  *    - `n_parcelamento`: número do parcelamento ou `null` se vazio.
  *    - `dataEntrada`: array de datas de entrada.
  * 
  * - Faz a requisição à API:
  *    - Utiliza `api.post()` para enviar os dados e aguarda a resposta da rota `/api/parcelamentos/index-parcelas`.
  * 
  * - Reseta os dados atuais:
  *    - Esvazia `this.obs.parcelas`.
  *    - Zera `this.obs.parcelas_length`.
  * 
  * - Processa a resposta:
  *    - Se `resp.data.itens.length === 0`:
  *      - Exibe um toast de aviso informando que nenhuma solicitação foi encontrada.
  *    - Caso contrário:
  *      - Atribui os itens retornados a `this.obs.parcelas`.
  *      - Atualiza `this.obs.parcelas_length` com o total de registros.
  * 
  * Observações:
  * - O bloco `catch` está vazio, portanto falhas na requisição não serão tratadas nem notificadas ao usuário.
  *   (Recomenda-se implementar um tratamento de erro mais robusto com `toast.error()` ou `console.error()`).
  * - A função é usada em fluxos onde há necessidade de listar parcelas filtradas e paginadas associadas a uma tarefa.
  * 
  * @param {number} paged Número da página a ser consultada.
  * @param {string} task_id ID da tarefa relacionada.
  * @param {string} tributo Tipo de tributo relacionado ao parcelamento.
  * @param {string} n_parcelamento Número do parcelamento (ou string vazia).
  * @param {string[]} dataEntrada Lista de datas de entrada utilizadas como filtro.
  * @returns {Promise<void>} Uma promise que se resolve após a atualização dos dados observáveis.
  */

  async handleTasksPagedParcelas(paged: number, task_id: string, tributo: string, n_parcelamento: string, dataEntrada: string[], statusParcela: string) {
    try {
      const data = {
        page: paged,
        limit: 5,
        task_id: task_id,
        tributo: tributo,
        n_parcelamento: n_parcelamento || null,
        dataEntrada: dataEntrada && dataEntrada.length === 2 ? dataEntrada : null,
        statusParcela: statusParcela || ""
      };

      let resp = await api.post("/api/parcelamentos/index-parcelas", data);

      this.obs.parcelas = resp.data.itens || [];
      this.obs.parcelas_length = resp.data.count || 0;

      if (resp.data.itens.length == 0) {
        toast.warning('Nenhuma parcela encontrada.', {});
      }

    } catch (ex) { }

  };

  /**
   * Busca os dados agregados de parcelamentos para geração de relatório/gráfico.
   *
   * Esta função:
   * - Ativa o estado de carregamento (`this.obs.loading`) durante a requisição.
   * - Normaliza o CNPJ: caso venha vazio/undefined, envia string vazia para a API (sem filtro por empresa).
   * - Realiza um POST para `api/central-acoes/parcelamentos/busca_parcelamentos` enviando `{ cnpj, tipo_p }`.
   * - Retorna a lista de itens agregados (ex.: por situação/status) para consumo no front.
   * - Em caso de erro, exibe um toast e retorna lista vazia.
   * - Garante o desligamento do loading no `finally`.
   *
   * @param {string} cnpjEmpresa CNPJ da empresa. Opcional: se vazio, busca parcelamentos de todas as empresas da conta.
   * @param {string} tipo_p Tipo do parcelamento (ex.: "1", "2"...). Opcional: se vazio, busca todos os tipos.
   *
   * @returns {Promise<any[]>} Lista de itens retornados pela API (resp.data.itens) ou [].
  **/

  async handleGerarParcelamentosRelatorio (cnpjEmpresa: string, tipo_p: string) {

    this.obs.loading = true;

    try {
      const cnpj = cnpjEmpresa?.trim() ? cnpjEmpresa : "";

      const resp = await api.post("api/central-acoes/parcelamentos/busca_parcelamentos", {
        cnpj,
        tipo_p: tipo_p,
      });

      return resp.data.itens || [];

    } catch (err) {

      toast.error("Erro ao buscar parcelamentos.", {});

      return [];

    } finally {

      this.obs.loading = false;

    }
  };

  /**
   * Edita ou atualiza um parcelamento associado a uma tarefa existente.
   * 
   * Esta função:
   * - Envia os dados de edição de uma tarefa para a API `/api/parcelamentos/create`.
   * - Exibe mensagens de sucesso com base na resposta da API.
   * - Atualiza o estado observável `this.obs.loading_tasks` para indicar o fim do carregamento.
   * 
   * Parâmetros:
   * - `id` (`string`): identificador da tarefa que será editada.
   * - `n_parcelamentos` (`string[]`): lista contendo os números dos parcelamentos a serem vinculados ou atualizados.
   * - `ativo` (`boolean`): indica se a tarefa está ativa (`true`) ou inativa (`false`).
   * - `dia_agendamento` (`number`): dia do mês configurado para o agendamento da tarefa.
   * 
   * Comportamento:
   * - Monta o objeto `data` com os seguintes campos:
   *    - `id`: ID da tarefa.
   *    - `n_parcelamentos`: array de números de parcelamento.
   *    - `ativo`: flag de ativação da tarefa.
   *    - `dia_agendamento`: dia de agendamento da tarefa.
   * 
   * - Realiza uma chamada à API:
   *    - Envia os dados via `api.post()` para a rota `/api/parcelamentos/create`.
   * 
   * - Processa a resposta:
   *    - Se `resp.data.ok` for `true`, exibe uma notificação de sucesso informando que o parcelamento foi atualizado com sucesso.
   *    - Caso contrário, exibe uma mensagem com o conteúdo de `resp.data.msg`.
   * 
   * - Finaliza a operação:
   *    - Define `this.obs.loading_tasks = false`, indicando o fim do processo de edição.
   * 
   * Observações:
   * - O bloco `catch` está vazio, portanto falhas na requisição não serão tratadas nem notificadas ao usuário.
   *   (É recomendável implementar um tratamento de erro adequado com `toast.error()` ou `console.error()`).
   * - A função é utilizada geralmente em componentes de edição de tarefas relacionadas a parcelamentos.
   * 
   * @param {string} id ID da tarefa que será editada.
   * @param {string[]} n_parcelamentos Lista de números de parcelamento vinculados à tarefa.
   * @param {boolean} ativo Indica se a tarefa está ativa ou não.
   * @param {number} dia_agendamento Dia do mês configurado para o agendamento da tarefa.
   * @returns {Promise<void>} Uma promise que se resolve após a tentativa de edição da tarefa.
  */

  async handleTasksEditTasks(id: string, n_parcelamentos: string[], ativo: boolean, dia_agendamento: number) {
    try {

      let data = {
        id: id,
        n_parcelamentos: n_parcelamentos,
        ativo: ativo,
        dia_agendamento: dia_agendamento
      }

      let resp = await api.post("/api/parcelamentos/create", data);

      if (resp.data.ok) {

        toast.success('Parcelamento atualizadso com sucesso!', {});

      } else {

        toast.success(resp.data.msg, {});

      }

    } catch (ex) { }

    this.obs.loading_tasks = false
  };

  /**
  * Realiza a busca e atualização da lista de agendamentos.
  * 
  * Este método faz uma requisição à API para obter os agendamentos com base em filtros predefinidos.
  * Os resultados são armazenados nos observáveis correspondentes para exibição e manipulação no sistema.
  * 
  * @param {number} page Número da página que será buscada.
  * 
  * @example
  * const params = {
  *   page: 1,
  *   limit: 5,
  *   recorrente: true
  * };
  *
  * const response = await api.post('/api/cnds/agendamento/index', params);
  */
  async handleIndexAgendamentos(page: number) {

    let params = {
      page: page,
      limit: 5,
      recorrente: this.obs.agendamentos_filter
    }

    let resp = await api.post(`/api/cnds/agendamento/index`, params);

    this.obs.index_agendamentos = []
    this.obs.length_agendamentos = 0

    if (resp.data.itens.length > 0) {

      this.obs.index_agendamentos = resp.data.itens

      this.obs.length_agendamentos = resp.data.count

    }
  };

  /**
   * Cria um agendamento de CNDS com os itens e informações fornecidas.
   * @param {any} params Um mapa contendo os dados do agendamento, como `dia`, `mes`, `ano`, etc. 
   * 
   * @example
   * const params = {
   *   [{cnpj: "12345678000190", ie: "123456789"}],
   *   dia: 15,
   *   mes: 12,
   *   ano: 2024,
   *   recorrente: false,
   *   gratuito: false
   * }
   * 
   * const response = await api.post('/api/cnds/create', params);
   */
  async handleSolicitaParcelamentos(params: any) {

    try {

      let resp = await api.post("/api/parcelamentos/create", params);

      if (resp.data.ok) {

        toast.success('Solicitação realizada com sucesso!', {});

      } else {

        toast.error(resp.data.msg, {});

      }

    } catch (ex) {

      toast.error("Erro ao realizar Solicitação, tente novamente mais tarde.", {});

    }

  };

  /**
   * Deleta um agendamento relacionado à CNDS.
   * @param {string} id Identificador do agendamento a ser deletado.
   * 
   * @example
   * const id = "6761ce19453393b716b46f9b"
   *
   * const response = await api.delete(`/api/cnds/agendamento/${id}`)
   * 
   */
  async handleDeleteAgendamento(id: string) {
    try {

      let resp = await api.delete(`/api/parcelamentos/agendamento/${id}`);

      if (resp.data.ok) {

        toast.success('Agendamento deletado com sucesso.', {});
        this.handleIndexAgendamentos(1)

      } else {

        toast.error('Erro ao deletar agendamento, tente novamente mais tarde.', {});

      }

    } catch (ex) {

      toast.error('Erro ao deletar agendamento, tente novamente mais tarde.', {});

    }
  };

  /**
   * Realiza o download de uma certidão CNDS.
   * @param {string} cnds_id Identificador da solicitação. 
   * @param {string} cnpj CNPJ da empresa.
   * @param {string} tipo Tipo da certidão ("receita", "tst", "caixa" e "sefa"). 
   * 
   * @example
   * const params = { 
   *   id: "6761ce19453393b716b46f9b"
   * }
   *
   * const response = await api.post(`/api/cnds/download`, params);
   * 
   */
  async downloadArquivos(task_id: string, n_parcelamento: string, n_parcela: number, tributo: string) {

    this.obs.loading_arquivos = true

    try {

      let params = {
        task_id: task_id,
        n_parcelamento: n_parcelamento,
        n_parcela: n_parcela,
        tributo: tributo
      }

      let resp = await api.post(`/api/parcelamentos/download`, params);

      if (resp.data.file) {

        const linkSource = `data:application/pdf;base64,${resp.data.file}`;
        const downloadLink = document.createElement("a");
        const fileName = `${task_id}${n_parcelamento}${n_parcela}${tributo}.pdf`;
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();

      } else {

        toast.error('Não foi possível realizar o download.', {});

      }

    } catch (ex) {

      toast.error('Erro Download Arquivo, tente novamente mais tarde.', {});

    }

    this.obs.loading_arquivos = false

  };

  /**
   * Realiza a compra de créditos para realizar a solicitação da certidação "receita".
   * @param {number} quantidade Quantidade de créditos a serem comprados.
   * @returns {boolean} Retorna true` solicitção de crédito foi concluida e false` caso houve algum erro.
   * 
   * @example
   * 
   * const quantidade = 30
   * 
   * const params = { 
   *    quantidade: quantidade
   * }
   * 
   * const response = await api.post(`/api/cnds/buy/credits`, params);
   * 
   */
  async handleBuyQuantidade(quantidade: number) {

    this.obs.loading = true

    try {

      let params = {
        quantidade: quantidade
      }

      let resp = await api.post("/api/parcelamentos/buy/credits", params);

      if (resp.data.ok) {

        this.obs.qrcode = resp.data.qrcode
        this.obs.pix_id = resp.data.pix_id
        this.obs.loading = false
        return true

      } else {

        toast.error(resp.data.msg, {});
        this.obs.loading = false
        return false

      }

    } catch (ex) {

      toast.error('Erro ao processar Compra, tente novamente mais tarde!', {});
      this.obs.loading = false
      return false

    }

  };

  /**
   * Atualiza um agendamento de CNDS com os itens e informações fornecidas.
   * @param {any} params Um mapa contendo os dados da solicitação, como `dia`, `mes`, `ano`, etc. 
   * @returns {boolean} Retorna `true` se o agendamento foi atualizado com sucesso, ou `false` caso tenha ocorrido algum erro.
   * 
   * @example
   * const params = {
   *   [{cnpj: "12345678000190", ie: "123456789"}],
   *   dia: 15,
   *   mes: 12,
   *   ano: 2024,
   *   recorrente: false,
   *   gratuito: false
   * }
   * 
   * const response = await api.post('/api/cnds/update', params);
   */
  async handleUpdateAgendamento(params: any) {

    try {

      let resp = await api.post("/api/parcelamentos/update", params);

      if (resp.data.ok) {

        toast.success('Agendamento atualizado com sucesso!', {});

        return true

      } else {

        toast.error(resp.data.msg, {});

      }

    } catch (ex) {

      toast.error("Erro ao atualizar agendamento, tente novamente mais tarde.", {});

    }

    return false

  };

  /**
  * Busca todas as tarefas ativas da empresa para preencher o formulário
  * @param cnpj CNPJ da empresa
  * @returns Lista de tarefas ativas
  */
  async getTarefasAtivasPorEmpresa(cnpj: string): Promise<any[]> {
    try {
      const data = {
        page: 1,
        limit: 100,
        cnpj: cnpj,
        ativo: true
      };

      const resp = await api.post("/api/parcelamentos/index", data);

      return resp.data.itens || [];

    } catch (error) {
      toast.error("Erro ao consultar tarefas ativas.", {});
      return [];
    }
  }

  /**
   * Realiza o download das Guias de parcelamentos de um ou mais parcelamentos
   * 
   * @param empresas Lista de CNPJs
   * @param situacao Situação das parcelas: ("" -> Todas, pendentes -> Pendentes, atrasadas -> Atrasadas)
   * @param task_id Id da tarefa quando for parcelas de uma Tarefa especifica
   * @returns arquivo .zip das parcelas
   */
  async handleDownloadAllGuias(empresas: string[], situacao: string, task_id: string | null) {

    try {

      const fileName = AppStore.obs1.contaId

      let resp = await api.post(`/api/parcelamentos/download-lote`, { task_id, empresas, situacao }, { responseType: 'blob' });

      if (resp.status == 200) {

        const name = `${fileName}.zip`

        downloadBinaryFile(resp.data, name)

      } else if (resp.status == 422) {

        const name = `${fileName}.xlsx`

        downloadBinaryFile(resp.data, name)

      } else {

        toast.error(decodeUtf8String(resp.headers?.msg || "Erro ao processar download, tente novamente mais tarde!"), {});

      }

    } catch (e) {}

    return null

  }

}

export const ParcelamentosStore = new _ParcelamentoStore();
